import React, { useEffect, useState } from "react";
import axios from "axios";
import { Autocomplete, CircularProgress } from "@mui/material";
import { StyledTextField } from "../StyledComponents";

// SNOMED administration method concepts ("Swallow", "Inject", "Instill", ...)
// live in the qualifier value hierarchy, same as the frequency lookup.
const METHOD_SEMANTIC_TAG = "qualifier value";

const MethodAutocomplete = ({
  value,
  onChange,
  label = "Method",
  placeholder = "Search method",
  error = false,
  helperText = "",
}) => {
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }

    const timerOut = setTimeout(async () => {
      const authToken = sessionStorage.getItem("accessToken") || "";
      const postBody = {
        term: term.toLowerCase(),
        state: "active",
        semantictag: METHOD_SEMANTIC_TAG,
        acceptability: "synonyms",
        returnlimit: "100",
        refsetid: null,
        parentid: null,
        excludeparentconcept: false,
        fullconcept: true,
      };
      try {
        setLoading(true);
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (
          response?.data?.status === "Success" &&
          response?.data?.data?.length > 0
        ) {
          const dataToProcess =
            response.data.data.length > 300
              ? response.data.data.slice(0, 300)
              : response.data.data;
          setOptions(
            dataToProcess.map((item) => ({
              label: item.term,
              id: item.conceptId,
            }))
          );
        } else {
          setOptions([]);
        }
      } catch (err) {
        console.log(err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timerOut);
  }, [inputValue, REACT_APP_BACKEND_ROUTE_URL]);

  return (
    <Autocomplete
      options={options}
      value={value ?? null}
      loading={loading}
      filterOptions={(option) => option} // options are already filtered by SNOMED
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      onChange={(event, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, selected) => option.id === selected?.id}
      noOptionsText={
        inputValue.trim().length < 2 ? "Type to search" : "No matches found"
      }
      slotProps={{
        paper: {
          sx: {
            "& .MuiAutocomplete-listbox": {
              fontSize: "12px",
            },
          },
        },
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MethodAutocomplete;
